import {createAsyncThunk} from '@reduxjs/toolkit'
import axios from 'axios'
import {
  zadCustomerReturnByIdUrl,
  zadCustomerReturnCancelUrl,
  zadCustomerReturnReturnableUrl,
  zadCustomerReturnsUrl,
  zadCustomerReturnUpdateUrl,
  zadCustomerReturnValidateUrl,
  zadCustomersUrl,
  zadSaleByIdUrl,
  zadSalesUrl,
} from '../../api'
import {parseZadApiError} from '../zad/errors'
import {
  sanitizeZadCustomerReturnUpdatePayload,
  sanitizeZadCustomerReturnWritePayload,
} from '../zad/customerReturnHelpers'
import {buildZadListQuery} from '../zad/query'
import {
  CancelZadCustomerReturnRequest,
  CreateZadCustomerReturnRequest,
  FetchZadCustomerReturnByIdRequest,
  FetchZadCustomerReturnPickerRequest,
  FetchZadCustomerReturnsRequest,
  FetchZadReturnableRequest,
  UpdateZadCustomerReturnRequest,
  ValidateZadCustomerReturnRequest,
} from './types'

export const fetchZadCustomerReturns = createAsyncThunk(
  'zadCustomerReturns/fetch',
  async (payload: FetchZadCustomerReturnsRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${zadCustomerReturnsUrl}?${buildZadListQuery(payload)}`)
      const {returns, total, totalPages} = res.data || {}
      return {
        success: true as const,
        returns: Array.isArray(returns) ? returns : [],
        total: typeof total === 'number' ? total : 0,
        totalPages: typeof totalPages === 'number' ? totalPages : 0,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadCustomerReturnById = createAsyncThunk(
  'zadCustomerReturns/fetchById',
  async (payload: FetchZadCustomerReturnByIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadCustomerReturnByIdUrl(payload.id))
      return {
        success: true as const,
        customerReturn: res.data.customerReturn,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadSaleReturnable = createAsyncThunk(
  'zadCustomerReturns/fetchReturnable',
  async (payload: FetchZadReturnableRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadCustomerReturnReturnableUrl(payload.saleId))
      const data = res.data || {}
      return {
        success: true as const,
        returnable: {
          saleId: String(data.saleId || payload.saleId),
          saleNumber: data.saleNumber,
          customer: data.customer || null,
          items: Array.isArray(data.items) ? data.items : [],
        },
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const createZadCustomerReturn = createAsyncThunk(
  'zadCustomerReturns/create',
  async (payload: CreateZadCustomerReturnRequest, {rejectWithValue}) => {
    try {
      const body = sanitizeZadCustomerReturnWritePayload(payload)
      const res = await axios.post(zadCustomerReturnsUrl, body)
      return {
        success: true as const,
        customerReturn: res.data.customerReturn,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const updateZadCustomerReturn = createAsyncThunk(
  'zadCustomerReturns/update',
  async (payload: UpdateZadCustomerReturnRequest, {rejectWithValue}) => {
    try {
      const {id, ...rest} = payload
      const body = sanitizeZadCustomerReturnUpdatePayload(rest)
      const res = await axios.post(zadCustomerReturnUpdateUrl(id), body)
      return {
        success: true as const,
        customerReturn: res.data.customerReturn,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const validateZadCustomerReturn = createAsyncThunk(
  'zadCustomerReturns/validate',
  async (payload: ValidateZadCustomerReturnRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadCustomerReturnValidateUrl(payload.id), {})
      return {
        success: true as const,
        customerReturn: res.data.customerReturn,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const cancelZadCustomerReturn = createAsyncThunk(
  'zadCustomerReturns/cancel',
  async (payload: CancelZadCustomerReturnRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadCustomerReturnCancelUrl(payload.id), {
        reason: String(payload.reason || ''),
      })
      return {
        success: true as const,
        customerReturn: res.data.customerReturn,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadCustomerReturnPickerSales = createAsyncThunk(
  'zadCustomerReturns/fetchPickerSales',
  async (payload: FetchZadCustomerReturnPickerRequest | void, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        `${zadSalesUrl}?${buildZadListQuery({
          pageNumber: 1,
          pageCount: 100,
          sortField: 'saleDate',
          sortOrder: 'desc',
          status: 'VALIDATED',
          search: payload?.search || '',
        })}`
      )
      const {sales} = res.data || {}
      return {
        success: true as const,
        sales: Array.isArray(sales) ? sales : [],
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadCustomerReturnPickerCustomers = createAsyncThunk(
  'zadCustomerReturns/fetchPickerCustomers',
  async (payload: FetchZadCustomerReturnPickerRequest | void, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        `${zadCustomersUrl}?${buildZadListQuery({
          pageNumber: 1,
          pageCount: 100,
          sortField: 'createdAt',
          sortOrder: 'desc',
          search: payload?.search || '',
        })}`
      )
      const {customers} = res.data || {}
      return {
        success: true as const,
        customers: Array.isArray(customers) ? customers : [],
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadCustomerReturnSaleById = createAsyncThunk(
  'zadCustomerReturns/fetchSaleById',
  async (payload: {id: string}, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadSaleByIdUrl(payload.id))
      return {
        success: true as const,
        sale: res.data.sale,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)
